import { useMemo } from "react";

import useTranslations from "@/shared/lang/useTranslations";

import {
  CRYPTO_SWAP_NETWORKS,
  SELL_STATUS_LABEL_KEYS,
  SELL_UNAVAILABLE_REASON_KEYS,
  STATUS_LABEL_KEYS,
  UNAVAILABLE_REASON_KEYS,
  type CryptoSwapNetworkRule,
} from "../constants/cryptoSwaps.constants";
import type { OtcSellOrderStatus } from "../types/cryptoSell.types";
import type { OtcOrderStatus } from "../types/cryptoSwaps.types";

/** Enum → words, with one fallback everywhere: a value we have not been taught renders raw, never hidden. */
export function useCryptoSwapLabels() {
  const { t } = useTranslations("cryptoSwaps");

  return useMemo(
    () => ({
      network(code: string | null | undefined): string {
        if (!code) return "";
        return CRYPTO_SWAP_NETWORKS[code]?.label ?? code;
      },

      networkShort(code: string | null | undefined): string {
        if (!code) return "";
        return CRYPTO_SWAP_NETWORKS[code]?.shortLabel ?? code;
      },

      /** Null for a network this UI was not taught: nothing about it is guessed. */
      networkRule(code: string | null | undefined): CryptoSwapNetworkRule | null {
        return code ? (CRYPTO_SWAP_NETWORKS[code] ?? null) : null;
      },

      txUrl(network: string | null | undefined, hash: string | null | undefined): string | null {
        if (!network || !hash) return null;
        return CRYPTO_SWAP_NETWORKS[network]?.txUrl(hash) ?? null;
      },

      status(status: OtcOrderStatus): string {
        const key = STATUS_LABEL_KEYS[status];
        return key ? t(key) : status;
      },

      sellStatus(status: OtcSellOrderStatus): string {
        const key = SELL_STATUS_LABEL_KEYS[status];
        return key ? t(key) : status;
      },

      unavailableReason(reason: string | null | undefined, asset: string): string {
        const key = reason ? UNAVAILABLE_REASON_KEYS[reason] : undefined;
        return t(key ?? "pair_unavailable_generic", { asset });
      },

      sellUnavailableReason(reason: string | null | undefined, asset: string): string {
        const key = reason ? SELL_UNAVAILABLE_REASON_KEYS[reason] : undefined;
        return t(key ?? "sell_unavailable_generic", { asset });
      },
    }),
    [t],
  );
}
